import Button from '@cobliteam/colib-ui/Button';
import Checkbox from '@cobliteam/colib-ui/Checkbox';
import { cn } from '@cobliteam/colib-ui/cn';
import Empty, { EmptyDescription, EmptyHeader, EmptyMedia } from '@cobliteam/colib-ui/Empty';
import InputGroup, { InputGroupAddon, InputGroupInput } from '@cobliteam/colib-ui/InputGroup';
import Label from '@cobliteam/colib-ui/Label';
import { Location, type VehicleGroup } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import { Typography } from '@cobliteam/ui/components';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Icon from '@cobliteam/ui/icons';
import TooltipOnIntent from '@cobliteam/ui-business/components/TooltipOnIntent/TooltipOnIntent';
import type React from 'react';
import { useState } from 'react';

export interface MultiSelectVehicleGroupsSidebarOverlayProps {
  isLoading?: boolean;
  vehicleGroups: VehicleGroup[];
  selectedGroups: VehicleGroup[];
  selectionChanged: boolean;
  disableGroupsWithoutVehicle?: boolean;
  limit?: number;
  onChange: (group: VehicleGroup) => void;
  onClearFilter: () => void;
  onApplyFilter: () => void;
  onCancel: () => void;
}

const MultiSelectVehicleGroupsSidebarOverlay: React.FC<MultiSelectVehicleGroupsSidebarOverlayProps> = ({
  isLoading,
  selectedGroups,
  vehicleGroups,
  selectionChanged,
  disableGroupsWithoutVehicle = true,
  limit,
  onChange,
  onClearFilter,
  onApplyFilter,
  onCancel,
}) => {
  const { t } = useTranslation('multiselects');
  const [searchBoxInput, setSearchBoxInput] = useState('');
  const [selectedGroupsDraft] = useState<VehicleGroup[]>(selectedGroups);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [_sortOrderLabel, setSortOrderLabel] = useState('A-Z');

  const { isAccessAllowed } = usePermissions();
  const hasUpdateVehiclePermission = isAccessAllowed('create-update-delete:vehicle');

  const handleCreateGroup = () => {
    window.open(`${window.location.origin}${Location.VEHICLES_GROUPS}/new`, '_blank')!.focus();
  };

  const renderNoGroupsPlaceholder = () => (
    <div>
      <div className='m-7 flex flex-col flex-wrap items-center justify-center gap-3'>
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <Icon name='cb-placeholder-vehicle-group' size='md' />
            </EmptyMedia>
            <EmptyDescription>{vehicleGroups.length === 0 ? t('groups.no_groups_registered') : t('groups.no_group_with_name')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
        {hasUpdateVehiclePermission && (
          <Button size='sm' onClick={handleCreateGroup}>
            {t('groups.create_groups')}
          </Button>
        )}
      </div>
    </div>
  );

  const swapSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setSortOrderLabel(sortOrder === 'asc' ? t('groups.sort_za') : t('groups.sort_az'));
  };

  const sortByName = (a: VehicleGroup, b: VehicleGroup) => {
    if (sortOrder === 'asc') return a.name.toLowerCase().localeCompare(b.name.toLowerCase());

    return b.name.toLowerCase().localeCompare(a.name.toLowerCase());
  };

  const sortBySelected = (a: VehicleGroup, b: VehicleGroup) => {
    if (selectedGroupsDraft.find((g) => g.id === a.id) && !selectedGroupsDraft.find((g) => g.id === b.id)) return -1;
    if (!selectedGroupsDraft.find((g) => g.id === a.id) && selectedGroupsDraft.find((g) => g.id === b.id)) return 1;
    return 0;
  };

  const filterOptionsBySearchBoxInput = () => {
    const normalizedSearchBoxInput = searchBoxInput.trim().toLowerCase();
    if (normalizedSearchBoxInput.length > 0) {
      return vehicleGroups.filter((group) => group.name.toLocaleLowerCase().includes(normalizedSearchBoxInput));
    }
    return [...vehicleGroups];
  };

  const filteredOptions = filterOptionsBySearchBoxInput();

  const groupsFiltered: VehicleGroup[] = filteredOptions.sort(sortByName).sort(sortBySelected);

  const validateGroupHasNoVehicle = (group: VehicleGroup): boolean => (disableGroupsWithoutVehicle && group.vehicleIds && group.vehicleIds.length === 0) ?? false;

  const disabled = (group: VehicleGroup) =>
    validateGroupHasNoVehicle(group) || (selectedGroups.find((g) => g.id === group.id) === undefined && limit != null && selectedGroups.length >= limit);

  return (
    <Dropdown.Overlay className='w-fit bg-popover text-popover-foreground'>
      <Dropdown.Header className='px-2'>
        <div className='flex w-full flex-row justify-between'>
          <Button variant='ghost' size='sm' data-testid='vehicle-group-filter-clean' disabled={selectedGroups.length === 0} onClick={onClearFilter}>
            {t('groups.clear_selection')}
          </Button>
          <Button variant='ghost' size='sm' data-testid='vehicle-group-filter-sort' onClick={swapSortOrder}>
            <Icon name='cb-sort-menu-asc' />
            {sortOrder === 'asc' ? t('groups.sort_az') : t('groups.sort_za')}
          </Button>
        </div>
      </Dropdown.Header>
      <Dropdown.Search className='px-2'>
        <InputGroup>
          <InputGroupAddon>
            <Icon name='search' size='sm' />
          </InputGroupAddon>
          <InputGroupInput
            data-testid='vehicle-group-filter-search-input'
            autoFocus
            aria-label={t('groups.search_placeholder')}
            placeholder={t('groups.search_placeholder')}
            value={searchBoxInput}
            onChange={(event) => setSearchBoxInput(event.target.value)}
          />
        </InputGroup>
      </Dropdown.Search>
      <Dropdown.Body className='px-2'>
        <Dropdown.List
          heightList={300}
          heightItem={28}
          data={groupsFiltered}
          isLoading={isLoading}
          placeholderComponent={renderNoGroupsPlaceholder()}
          loadingComponent={<Dropdown.Loading />}
          itemComponent={(group: VehicleGroup) => {
            const checkbox = (
              <div className='flex items-center gap-2'>
                <Checkbox
                  id={group.name}
                  data-testid={`vehicle-group-filter-${group.id}`}
                  data-cy={`vehicle-group-filter-${group.id}`}
                  disabled={disabled(group)}
                  checked={selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)}
                  onCheckedChange={() => onChange(group)}
                  value={group.name}
                  title={group.name}
                />
                <Label htmlFor={group.name} className={cn('line-clamp-1 text-start', disabled(group) && 'text-muted-foreground')} title={group.name}>
                  {group.name}
                </Label>
              </div>
            );

            return (
              <div key={group.name} className='line-clamp-1 w-full pr-4'>
                <TooltipOnIntent render={checkbox} disabled={!validateGroupHasNoVehicle(group)}>
                  {t('groups.group_without_vehicles')}
                </TooltipOnIntent>
              </div>
            );
          }}
        />
      </Dropdown.Body>
      <Dropdown.Footer>
        <Dropdown.Actions className='flex flex-row justify-between px-2'>
          <div className='flex flex-row'>
            <Typography className='font-bold text-popover-foreground'>{`${selectedGroups.length}${limit ? `/${limit}` : ''}`}</Typography>
            &nbsp;
            <Typography className='text-popover-foreground'>{t('groups.selected')}</Typography>
          </div>
          <div className='ml-4 flex flex-row'>
            <Button variant='ghost' size='sm' data-testid='vehicle-group-filter-cancel' onClick={onCancel} className='px-4'>
              {t('groups.cancel')}
            </Button>
            <Button
              size='sm'
              data-testid='vehicle-group-filter-apply'
              onClick={onApplyFilter}
              disabled={vehicleGroups.length === 0 || !selectionChanged || selectedGroups.length > 30}
              className='ml-2 px-7'
            >
              {t('groups.apply')}
            </Button>
          </div>
        </Dropdown.Actions>
      </Dropdown.Footer>
    </Dropdown.Overlay>
  );
};

export default MultiSelectVehicleGroupsSidebarOverlay;
